import Phaser from 'phaser';
import { RiftScar as RiftScarEntity } from '../entities/RiftScar';
import { RiftScar as RiftScarData, RiftTraversalResult } from '../../types/rifts';

export class RiftScarManager {
  private scene: Phaser.Scene;
  private scars: Map<string, RiftScarEntity> = new Map();
  private apiBaseUrl: string;
  private currentRiftId: string;
  private nearestScar: RiftScarEntity | null = null;

  constructor(scene: Phaser.Scene, apiBaseUrl: string, currentRiftId: string) {
    this.scene = scene;
    this.apiBaseUrl = apiBaseUrl;
    this.currentRiftId = currentRiftId;
  }

  async loadScarsForRift(riftId: string): Promise<void> {
    try {
      console.log(`[RIFT SCARS] Loading scars for rift: ${riftId}`);
      const response = await fetch(`${this.apiBaseUrl}/api/rifts/${riftId}/scars`);
      if (!response.ok) {
        console.error(`[RIFT SCARS] Failed to fetch rift scars: HTTP ${response.status}`);
        return;
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('[RIFT SCARS] Server returned non-JSON response (endpoint may not exist yet)');
        return;
      }

      const scarsData: RiftScarData[] = await response.json();
      console.log(`[RIFT SCARS] Received ${scarsData.length} scars from API:`, scarsData.map(s => ({
        id: s.id,
        name: s.name,
        source: s.sourceRift,
        dest: s.destinationRift,
        pos: `(${s.worldX}, ${s.worldY})`,
        active: s.isActive
      })));
      
      this.clear();

      scarsData.forEach(scarData => {
        if (scarData.isActive) {
          const scar = new RiftScarEntity(this.scene, scarData);
          this.scars.set(scarData.id, scar);
          console.log(`🌊 Created rift scar: ${scarData.name} at (${scarData.worldX}, ${scarData.worldY}) [${scarData.sourceRift} → ${scarData.destinationRift}]`);
        } else {
          console.log(`⚠️ Skipped inactive scar: ${scarData.name}`);
        }
      });

      console.log(`🌊 Loaded ${this.scars.size} active rift scars for ${riftId}`);
    } catch (error) {
      console.error('Error loading rift scars:', error);
    }
  }

  update(time: number, delta: number, playerX: number, playerY: number): void {
    let closestScar: RiftScarEntity | null = null;
    let closestDistance = Infinity;

    this.scars.forEach(scar => {
      // Update animations
      scar.update(time, delta);

      // Update proximity and get distance
      const dist = scar.updateDistance(playerX, playerY);
      
      if (scar.isNearby()) {
        if (dist < closestDistance) {
          closestDistance = dist;
          closestScar = scar;
        }
      }
    });

    if (closestScar !== this.nearestScar) {
      this.nearestScar = closestScar;
      
      if (closestScar) {
        const scar = closestScar as RiftScarEntity;
        this.scene.events.emit('rift-scar-nearest', scar.getScarData());
      } else {
        this.scene.events.emit('rift-scar-none-nearby');
      }
    }
  }

  async traverseScar(characterId: string, scarData: RiftScarData): Promise<RiftTraversalResult | null> {
    const scar = this.scars.get(scarData.id);
    if (!scar) {
      console.warn(`Rift scar ${scarData.id} not found in manager`);
      return null;
    }

    if (scarData.stability === 'sealed') {
      this.scene.events.emit('rift-scar-sealed', scarData);
      return null;
    }

    try {
      console.log(`[RIFT TRAVERSAL] Starting traversal of scar: ${scarData.id}`);
      await scar.triggerEntry();

      console.log('[RIFT TRAVERSAL] Calling API to traverse scar:', scarData.id);
      const response = await fetch(`${this.apiBaseUrl}/api/rift-scars/${scarData.id}/traverse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ characterId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[RIFT TRAVERSAL] API error (${response.status}):`, errorText);
        throw new Error(`Traversal failed: ${response.status} - ${errorText}`);
      }

      const result: RiftTraversalResult = await response.json();
      console.log('[RIFT TRAVERSAL] API response:', result);
      
      if (result.success) {
        this.currentRiftId = result.destinationRift;
        console.log('[RIFT TRAVERSAL] Emitting rift-traversal-success event');
        this.scene.events.emit('rift-traversal-success', result);
        
        // Load scars for new rift
        await this.loadScarsForRift(result.destinationRift);
        
        // Fade back in
        console.log('[RIFT TRAVERSAL] Fading back in...');
        this.scene.cameras.main.fadeIn(1000);
        
        return result;
      } else {
        console.error('Traversal failed:', result.error);
        this.scene.cameras.main.fadeIn(500);
        return null;
      }
    } catch (error) {
      console.error('Error traversing rift scar:', error);
      this.scene.cameras.main.fadeIn(500);
      return null;
    }
  }

  async traverseNearestScar(characterId: string): Promise<RiftTraversalResult | null> {
    if (!this.nearestScar) {
      console.warn('No rift scar nearby to traverse');
      return null;
    }

    return this.traverseScar(characterId, this.nearestScar.getScarData());
  }

  getNearestScar(): RiftScarEntity | null {
    return this.nearestScar;
  }

  getCurrentRiftId(): string {
    return this.currentRiftId;
  }

  setCurrentRiftId(riftId: string): void {
    this.currentRiftId = riftId;
  }

  clear(): void {
    this.scars.forEach(scar => scar.destroy());
    this.scars.clear();
    this.nearestScar = null;
  }

  destroy(): void {
    this.clear();
  }
}
